import React, { useState, useEffect } from 'react';
import { QrCode, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { config } from '../config';

const WhatsAppAuthView = ({ onAuthSuccess }: { onAuthSuccess?: () => void }) => {
    const [qrCode, setQrCode] = useState(null);
    const [deviceId, setDeviceId] = useState(null);
    const [status, setStatus] = useState('loading'); // loading | connected | error
    const [error, setError] = useState<string | null>(null);

    const fetchQRCode = async () => {
        try {
            setError(null);
            setStatus('loading');

            // Step 1: Ensure device exists
            const deviceRes = await fetch(`${config.API_BASE_URL}/api/whatsapp/auth/create-device`, {
                method: 'POST',
            });
            if (!deviceRes.ok) {
                const errData = await deviceRes.json().catch(() => ({}));
                throw new Error(errData.error || `Device creation failed: ${deviceRes.status}`);
            }
            const deviceData = await deviceRes.json();
            if (deviceData.error) {
                throw new Error(deviceData.error);
            }

            const id = deviceData.deviceId;
            setDeviceId(id);

            // Step 2: Fetch QR Code using the deviceId
            const res = await fetch(`${config.API_BASE_URL}/api/whatsapp/auth/qrcode?deviceId=${id}`);
            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.error || `Server error: ${res.status}`);
            }
            const data = await res.json();
            if (data.error) {
                throw new Error(data.error);
            }
            if (!data.qrCode) {
                throw new Error('No QR code in server response');
            }
            setQrCode(data.qrCode);
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : String(e);
            setError(msg);
            setStatus('error');
        }
    };

    const pollStatus = async () => {
        if (!deviceId) {return;}
        try {
            const res = await fetch(`${config.API_BASE_URL}/api/whatsapp/auth/status?sessionId=default_session&deviceId=${deviceId}`);
            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.error || `Failed to check status: ${res.status}`);
            }
            const data = await res.json();
            if (data.status === 'connected') {
                setStatus('connected');
                if (onAuthSuccess) {
                    setTimeout(onAuthSuccess, 2000);
                }
            }
        } catch (e) {
            console.error('Polling error:', e);
            // We don't set status to 'error' here to avoid flickering while QR is still shown,
            // but we log the error.
        }
    };

    useEffect(() => {
        fetchQRCode();
        const interval = setInterval(pollStatus, 5000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="max-w-md mx-auto text-center space-y-8 py-12">
            <div className="space-y-4">
                <div className="w-20 h-20 bg-green-100 text-green-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
                    <QrCode size={40} />
                </div>
                <h2 className="text-3xl font-black text-slate-800 uppercase tracking-tight">Link WhatsApp</h2>
                <p className="text-slate-500 font-medium leading-relaxed text-sm px-6">
                    Connect your WhatsApp account to enable automated OTP delivery and AI-powered chat assistants.
                </p>
            </div>

            <div className="bg-white p-8 rounded-[3rem] shadow-2xl border border-slate-100 flex flex-col items-center space-y-6 animate-in zoom-in-95 duration-500">
                {status === 'loading' && !qrCode && (
                    <div className="flex flex-col items-center gap-4 py-12">
                        <Loader2 size={40} className="text-blue-600 animate-spin" />
                        <p className="text-slate-400 font-bold text-sm uppercase tracking-widest">Generating QR Code...</p>
                    </div>
                )}

                {qrCode && status !== 'connected' && (
                    <div className="space-y-6 w-full">
                        <div className="bg-white p-4 rounded-2xl border-2 border-slate-100 shadow-inner flex justify-center">
                            <img src={qrCode} alt="WhatsApp QR Code" className="w-64 h-64 object-contain" />
                        </div>
                        <div className="flex items-center justify-center gap-3 text-slate-500 font-bold text-sm">
                            <Loader2 size={16} className="animate-spin" />
                            <span>Waiting for authorization...</span>
                        </div>
                    </div>
                )}

                {status === 'connected' && (
                    <div className="flex flex-col items-center gap-4 py-12 animate-in fade-in slide-in-from-top-4">
                        <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-2">
                            <CheckCircle size={48} />
                        </div>
                        <h3 className="text-xl font-black text-slate-800 uppercase">Successfully Linked!</h3>
                        <p className="text-slate-500 text-sm">Your WhatsApp is now connected to the Business OS.</p>
                    </div>
                )}

                {status === 'error' && (
                    <div className="flex flex-col items-center gap-4 py-12 text-red-500">
                        <AlertCircle size={40} />
                        <p className="font-bold text-sm uppercase">{error}</p>
                        <button onClick={fetchQRCode} className="text-xs underline font-bold">Try Again</button>
                    </div>
                )}
            </div>

            <div className="bg-blue-50 p-6 rounded-3xl border border-blue-100 text-left">
                <h4 className="text-blue-800 font-black text-xs uppercase tracking-widest mb-3 flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-600 rounded-full" />
                    How to link
                </h4>
                <ol className="text-blue-700/80 text-xs space-y-3 font-medium list-decimal pl-4">
                    <li>Open WhatsApp on your phone.</li>
                    <li>Go to <strong>Settings</strong> {'>'} <strong>Linked Devices</strong>.</li>
                    <li>Tap <strong>Link a Device</strong> and scan the QR code above.</li>
                    <li>Wait for the status to update to "Connected".</li>
                </ol>
            </div>
        </div>
    );
};

export default WhatsAppAuthView;
